import { GETREQUESTOPTIONS, instance } from '../apiService';

export type VariableType = 'fixed' | 'ref';

export interface Variable {
    type: VariableType;
    name: string;
    label?: string;
    category?: string;
    fixedValue?: string;
    refTarget?: string;
    refId?: string;
}

export type VariableTab = 'contacts' | 'departmentInfo';

export type CategoryKind = 'fixed' | 'legacy' | 'stored' | 'other';

export interface SettingsCategory {
    key: string;
    tab: VariableTab;
    kind: CategoryKind;
    label?: string;
}

export interface SettingsVariable {
    name: string;
    type: VariableType;
    field: 'ref' | 'text' | 'number';
    category: string;
    label?: string;
    fixedValue?: string;
    refId?: string;
    stored: boolean;
    fixed: boolean;
    renamable: boolean;
}

export interface DepartmentVariableSettings {
    categories: SettingsCategory[];
    variables: SettingsVariable[];
}

export interface CreateVariableInput {
    type: VariableType;
    label: string;
    category: string;
    fixedValue?: string;
    refId?: string;
}

export interface UpdateVariableInput {
    label?: string;
    category?: string;
    fixedValue?: string;
    refId?: string;
}

const unwrapResponse = <T>(res: { status: number; data: T } | undefined): T => {
    if (!res || res.status >= 400) {
        throw res?.data ?? new Error('Request failed');
    }
    return res.data;
};

export type DepartmentVariableField =
    'text' | 'number' | 'boolean' | 'textarea' | 'ref';

export interface DepartmentVariableTemplateItem {
    field: DepartmentVariableField;
    name: string;
    refTarget?: string;
    required?: boolean;
}

export interface DepartmentVariableTemplateSection {
    key: string;
    variables: DepartmentVariableTemplateItem[];
}

export const getDepartmentVariables = async (): Promise<Variable[]> => {
    const res = await instance.get(
        '/api/department-variables',
        GETREQUESTOPTIONS()
    );
    return res.data;
};

export const getDepartmentVariableSettings =
    async (): Promise<DepartmentVariableSettings> => {
        const res = await instance.get(
            '/api/department-variables/settings',
            GETREQUESTOPTIONS()
        );
        return unwrapResponse(res);
    };

export const createVariable = async (
    input: CreateVariableInput
): Promise<Variable[]> => {
    const res = await instance.post(
        '/api/department-variables/variable',
        input,
        GETREQUESTOPTIONS()
    );
    return unwrapResponse(res);
};

export const updateVariable = async (
    name: string,
    input: UpdateVariableInput
): Promise<Variable[]> => {
    const res = await instance.put(
        `/api/department-variables/variable/${encodeURIComponent(name)}`,
        input,
        GETREQUESTOPTIONS()
    );
    return unwrapResponse(res);
};

export const removeVariable = async (name: string): Promise<void> => {
    const res = await instance.delete(
        `/api/department-variables/variable/${encodeURIComponent(name)}`,
        GETREQUESTOPTIONS()
    );
    unwrapResponse(res);
};

export const createCategory = async (input: {
    label: string;
    tab: VariableTab;
}): Promise<void> => {
    const res = await instance.post(
        '/api/department-variables/category',
        input,
        GETREQUESTOPTIONS()
    );
    unwrapResponse(res);
};

export const renameCategory = async (
    key: string,
    label: string
): Promise<void> => {
    const res = await instance.put(
        `/api/department-variables/category/${encodeURIComponent(key)}`,
        { label },
        GETREQUESTOPTIONS()
    );
    unwrapResponse(res);
};

export const removeCategory = async (key: string): Promise<void> => {
    const res = await instance.delete(
        `/api/department-variables/category/${encodeURIComponent(key)}`,
        GETREQUESTOPTIONS()
    );
    unwrapResponse(res);
};

export const resolveVariables = async (): Promise<Record<string, string>> => {
    const res = await instance.get(
        '/api/department-variables/resolve',
        GETREQUESTOPTIONS()
    );
    return res.data;
};

export const getDepartmentVariableTemplate = async (): Promise<
    DepartmentVariableTemplateSection[]
> => {
    const res = await instance.get(
        '/api/department-variables/template',
        GETREQUESTOPTIONS()
    );
    return res.data;
};

export interface AffectedPolicy {
    _id: string;
    title?: string;
    status?: string;
}

export const getAffectedPolicies = async (
    variableName: string
): Promise<AffectedPolicy[]> => {
    const res = await instance.get(
        `/api/department-variables/variable/${encodeURIComponent(variableName)}/affected-policies`,
        GETREQUESTOPTIONS()
    );
    return res.data.policies;
};
